import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { useListDiscountPolicies } from 'src/features/discountPolicies/api/useListDiscountPolicies';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import CreateDiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/CreateDiscountPolicyFormModal';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import CellDiscountPolicyActions from 'src/features/discountPolicies/components/tables/CellDiscountPolicyActions';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import React from 'react';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const DiscountPoliciesTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const [dateNow] = React.useState(() => Date.now());

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const permissionManageDiscounts = useHasPermissions([PERMISSION.MANAGE_DISCOUNTS]);

    const {
        data: discountPolicies,
        isLoading,
        isError,
    } = useListDiscountPolicies({
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
    });

    const columnHelper = createColumnHelper<DiscountPolicy>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('percent', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const percent = row.original.percent;
                const displayTaxPercent = (Number(percent) * 100).toFixed(2);
                return percent != null ? `${displayTaxPercent}%` : '-';
            },
            meta: {
                title: formatMessage(discountPoliciesMessages.percent),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.amount && row.original.currency
                    ? formatNumber(row.original.amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('service_plans', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.service_plans?.length ?? '-';
            },
            meta: {
                title: formatMessage(servicePlansMessages.servicePlans),
            },
        }),
        columnHelper.accessor('service_plan_items', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.service_plan_items?.length ?? '-';
            },
            meta: {
                title: formatMessage(servicePlanItemsMessages.servicePlanItems),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellDiscountPolicyActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<DiscountPolicy, unknown>
            columns={columns}
            data={discountPolicies}
            semantics={{
                title: formatMessage(discountPoliciesMessages.discountPolicies),
                entryName: formatMessage(discountPoliciesMessages.policy).toLowerCase(),
                entryNames: formatMessage(discountPoliciesMessages.policies).toLowerCase(),
                placeholder: formatMessage(discountPoliciesMessages.policy).toLowerCase(),
            }}
            search={search}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageDiscounts && { newEntry: CreateDiscountPolicyFormModal }),
            }}
        />
    );
};

export default DiscountPoliciesTable;
